import PropTypes from 'prop-types';
import React, { useState, useEffect, useCallback } from 'react';
import isEqual from 'lodash/isEqual';

// Excel
import ExcelJS from 'exceljs';
import { saveAs } from 'file-saver';

// @Mui
import { useSnackbar } from 'src/components/snackbar';
import { Box, Stack } from '@mui/system';
import Card from '@mui/material/Card';
import Table from '@mui/material/Table';
import Button from '@mui/material/Button';
import Container from '@mui/material/Container';
import TableContainer from '@mui/material/TableContainer';
import { CircularProgress, Grid, Icon, IconButton, Tooltip, Typography } from '@mui/material';
import TableBody from '@mui/material/TableBody';
import { useLocation } from 'react-router';
import { paths } from 'src/routes/paths';
import { useRouter } from 'src/routes/hooks';
import { denseHeight } from 'src/constants/TableRowHeight';
import { RouterLink } from 'src/routes/components';
import { useSettingsContext } from 'src/components/settings';
import { useBoolean } from 'src/hooks/use-boolean';
import {
  useTable,
  getComparator,
  emptyRows,
  TableNoData,
  TableSkeleton,
  TableEmptyRows,
  TableHeadCustom,
  TableSelectedAction,
  TablePaginationCustom,
} from 'src/components/table';
import axiosInstance from 'src/api/axiosInstance';
import Iconify from 'src/components/iconify';
import Scrollbar from 'src/components/scrollbar';
import BreadCrumbs from 'src/components/custom-breadcrumbs/breadcrumbs';
import useNavigateErrorHandler from 'src/hooks/useNavigateErrorHandler';
import { useDispatch, useSelector } from 'react-redux';
import { setFilters, setPreservedFilters } from 'src/redux/reducers/filtersReducer';
import { fetchEncryptedToken } from 'src/utils/encryptUtils';
import { decryptData } from 'src/utils/Crypto';
import { usePlanFeature } from 'src/hooks/usePlanFeature';
import { openUpgradeDialog } from 'src/redux/slices/planSlice';
import { FeatureCode, FEATURE_LABELS } from 'src/constants/FeatureCode';
import PlanBadge from 'src/components/plan-badge/PlanBadge';
import CandTableToolbar from '../cand-table-toolbar';
import CandTableRow from '../cand-table-row';
import CandListFilter from '../cand-list-filter';
import CandListFilterResult from '../cand-list-filter-result';
import ImportExcelDialog from '../importExcelDialog';

const TABLE_HEAD = [
  { id: 'fname', label: 'Candidate Name', position: 'sticky' },
  { id: 'email', label: 'Email Address', width: 160 },
  { id: 'city', label: 'City', width: 110 },
  { id: 'mob', label: 'Contact Number', width: 140, align: 'center' },
  { id: 'priSkl', label: 'Primary Skills', width: 140 },
  { id: 'candStatus', label: 'Candidate Status', width: 160 },
  { id: 'positionName', label: 'Position Applied', width: 160 },

  { id: '', label: 'Resume', width: 110, align: 'center' },
  { id: 'lastUpdtDate', label: 'Last Updated Date', width: 160, align: 'center' },
  { id: '', width: 88, position: 'sticky' },
];

const defaultFilters = {
  fname: [],
  email: [],
  positionName: [],
  gender: [],
  sourceOfHire: [],
  candStatus: [],
  city: [],
  primarySkill: [],
  Status: [],
  qualificationNames: [],
  stream: [],
  formStatus: '',
  experience: { from: '', to: '' },
};

const initialFilters = {
  ...defaultFilters,
  Status: ['Active'],
};

export default function CandidateListView({ fullAccess }) {
  const token = sessionStorage.getItem('token');
  const router = useRouter();

  const table = useTable({ defaultOrderBy: 'lastUpdtDate', defaultOrder: 'desc' });

  const settings = useSettingsContext();

  const [tableData, setTableData] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isFirstLoad, setIsFirstLoad] = useState(true);
  const confirm = useBoolean();
  const openFilters = useBoolean();
  const uploadCsv = useBoolean();

  const dispatch = useDispatch();
  const location = useLocation();

  const { value: maxCandidatePool, requiredPlan, type: featureType } = usePlanFeature(FeatureCode.MAX_CANDIDATE_POOL);
  const candidatePoolCount = tableData.length;
  const isLimitReached = maxCandidatePool !== null && maxCandidatePool !== undefined && Number(maxCandidatePool) !== -1 && candidatePoolCount >= Number(maxCandidatePool);
  const preservedFilters = useSelector(
    (state) => state.filters.preservedFilters[location.pathname]
  );

  const [filters, setFiltersState] = useState(preservedFilters || initialFilters);

  const [candNmArr, setCandNmArr] = useState([]);
  const [candEmailArr, setCandEmailArr] = useState([]);
  const [candPostionName, setCandPositionName] = useState([]);
  const [genderArr, setGenderArr] = useState([]);
  const [srcHireArr, setSrcHireArr] = useState([]);
  const [candStatusArr, setCandStatusArr] = useState([]);
  const [candCityArr, setCandCityArr] = useState([]);
  const [candPrimrSklArr, setCandPrimrSklArr] = useState([]);
  const handleApiError = useNavigateErrorHandler();
  const [candidateListRefresh, setCandidateListRefresh] = useState(false);
  const [qualArr, setQualArr] = useState([]); // Qualification list
  const [streamArr, setStreamArr] = useState([]); // Stream list
  const { enqueueSnackbar } = useSnackbar();
  const [experienceArr, setExperienceArr] = useState([]);

  useEffect(() => {
    const searchParams = new URLSearchParams(location.search);
    const filterTypes = searchParams.getAll('filterType');
    const filterValues = searchParams.getAll('filterValue');
    const singleFilterType = searchParams.get('filterType');
    const singleFilterValue = searchParams.get('filterValue');
    const cityParam = searchParams.get('city');

    if (cityParam) {
      const url = new URL(window.location);
      const newFilters = {
        ...(preservedFilters || defaultFilters),
        city: [cityParam],
      };
      setFiltersState(newFilters);
      dispatch(setPreservedFilters({ path: location.pathname, filters: newFilters }));

      url.searchParams.delete('city');
      window.history.replaceState({}, '', url.toString());
    } else if (filterTypes.length > 0 && filterValues.length > 0) {
      const url = new URL(window.location);
      const newFilters = { ...(preservedFilters || defaultFilters) };
      filterTypes.forEach((type, index) => {
        if (type) {
          const values = filterValues[index] ? filterValues[index].split(',') : [];
          newFilters[type] = values;
        }
      });
      setFiltersState(newFilters);
      dispatch(setPreservedFilters({ path: location.pathname, filters: newFilters }));

      url.searchParams.delete('filterType');
      url.searchParams.delete('filterValue');
      window.history.replaceState({}, '', url.toString());
    } else if (singleFilterType && singleFilterValue) {
      const url = new URL(window.location);
      const updatedFilters = { ...(preservedFilters || defaultFilters) };
      if (singleFilterType === 'experience') {
        const [minExp, maxExp] = singleFilterValue.split('-').map(Number);
        updatedFilters.experience = { from: minExp || '', to: maxExp || '' };
      } else {
        updatedFilters[singleFilterType] = singleFilterValue.split(',');
      }
      setFiltersState(updatedFilters);
      dispatch(setPreservedFilters({ path: location.pathname, filters: updatedFilters }));

      url.searchParams.delete('filterType');
      url.searchParams.delete('filterValue');
      window.history.replaceState({}, '', url.toString());
    } else if (preservedFilters && Object.keys(preservedFilters).length > 0) {
      setFiltersState(preservedFilters);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.search]);

  const formatDtTm = (dttm) => {
    const date = new Date(dttm);
    const options = {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
    };

    return date.toLocaleDateString('en-GB', options);
  };

  const sessionData = JSON.parse(localStorage.getItem('selectedSubscrSite'));
  const subscrSiteId = sessionData?.subscrSite?.subscrSiteId;

  const getArrData = async () => {
    setIsLoading(true);
    try {
      // API calls for candidate data and candidate details
      const fetchCandidates = axiosInstance.get(`/rest/candidate/getAllCand`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      const fetchCandidateDetails = axiosInstance.get(`/rest/candidate/getAllCandDetails`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      // Resolve both API calls
      const [candidatesRes, detailsRes] = await Promise.all([
        fetchCandidates,
        fetchCandidateDetails,
      ]);

      const candidates = candidatesRes.data;
      const details = detailsRes.data;

      // Process candidates and their details
      const mod_data = candidates.map((cnd) => {
        const prefLoc = details.preferredLocations
          .filter((pref) => pref.candidateId === cnd.candidateId)
          .map((pref) => pref.prefLocCity.cityName)
          .join(', '); // join multiple cities with comma

        const pSkl = details.pSkillDetails
          .filter((prSkl) => prSkl.candidateId === cnd.candidateId)
          .map((prSkl) => prSkl.skill.skillName);

        const sSkl = details.sSkillDetails
          .filter((sCSkl) => sCSkl.candidateId === cnd.candidateId)
          .map((sCSkl) => sCSkl.skill.skillName);
        const eduDtl = details.educationDetails.filter(
          (edu) => edu.candidateId === cnd.candidateId
        );
        const certDtl = details.certifications.filter(
          (cert) => cert.candidateId === cnd.candidateId
        );
        const expDtl = details.expDetails.filter((exp) => exp.candidateId === cnd.candidateId);
        const refDtl = details.refDetails.filter((ref) => ref.candidate === cnd.candidateId);

        return {
          id: cnd.candidateId,
          fname: `${cnd?.firstName || ''} ${cnd?.middleName || ''} ${cnd?.lastName || ''}`,
          email: decryptData(cnd.email),
          city: cnd?.currentLocationCity || '-',
          mob: decryptData(cnd.mobileNumber),
          mobCode: cnd?.mobPhoneCode || '-',
          gender: cnd?.gender || '-',
          prefLoc: prefLoc || '-',
          priSkl: pSkl.join(',').toString(), // correct
          secSkl: sSkl.join(',').toString(), // correct
          candStatus: cnd?.status || '-',
          uniqNumTyp: cnd?.uniqueNoType || '-',
          uniqNum: cnd?.panNo || '-',
          linkedinUrl: cnd?.linkedinUrl || '-',
          readyToRelocate: cnd?.readyToRelocate ? 'Yes' : 'No',
          resume: cnd.resume,
          highestQual: cnd?.qualification || '-',
          experience: cnd.experienceInYears,
          relExpYr: cnd.relevantExperience,
          curComp: cnd.currentEmployer,
          curJob: cnd?.currentJobTitle || '-',
          noticePrd: cnd.noticePeriod,
          curSal: cnd.currentSalary,
          curSalCurncy: cnd.currentSalaryCurrency || '',
          expSal: cnd.expectedSalary,
          expSalCurncy: cnd.expectedSalary || '',
          isCurEmp: cnd.currentEmployee ? 'Yes' : 'No',
          resign: cnd.hasResigned ? 'Yes' : 'No',
          resignDt: cnd.resignDate,
          canJoinBy: cnd.canJoinBy,
          srcOfHire: cnd?.sourceOfHire || '-',
          positionName: cnd?.status === 'Rejected' ? 'Not Approved' : cnd?.positionName || '-',
          originalPositionName: cnd?.positionName,
          srcOfHireNm: cnd.sourceOfHireName,
          blocked: cnd.rehire,
          eduDetails: eduDtl,
          certDetails: certDtl,
          expDetails: expDtl,
          refDetails: refDtl,
          lastUpdtDate: cnd?.lastUpdatedDate || '-',
          endDate: cnd?.endDate || '-',
          profilePhoto: cnd?.profilePhoto || null,
          profilePhotoUrl: cnd?.profilePhotoUrl || null,
          isFinal: cnd.finalCand ? 'Complete' : 'Incomplete',
          qualificationNames: eduDtl.length
            ? eduDtl
              .map((edu) => edu.qualification)
              .filter((q) => q)
              .join(', ')
            : '-',
        };
      });

      // Sort by last updated date (newest first)
      mod_data.sort((a, b) => new Date(b.lastUpdtDate || 0) - new Date(a.lastUpdtDate || 0));

      // Update table data
      setTableData(mod_data);

      // Extract and set filter arrays
      setGenderArr(
        Array.from(
          new Set(mod_data.flatMap((cand) => cand.gender).filter((status) => status !== '-'))
        )
      );

      setExperienceArr(
        Array.from(
          new Set(mod_data.flatMap((cand) => cand.expYr).filter((exp) => exp !== '-'))
        ).sort((a, b) => a - b) // Sort experiences in ascending order
      );

      setQualArr(
        Array.from(
          new Set(
            mod_data.flatMap(
              (cand) =>
                cand.eduDetails?.map((edu) => edu.qualification).filter((q) => q && q !== '-') || []
            )
          )
        )
      );

      setStreamArr(
        Array.from(
          new Set(
            mod_data.flatMap(
              (cand) =>
                cand.eduDetails?.map((edu) => edu.stream).filter((s) => s && s !== '-') || []
            )
          )
        )
      );

      setSrcHireArr(
        Array.from(
          new Set(mod_data.flatMap((cand) => cand.srcOfHire).filter((src) => src && src !== '-'))
        )
      );
      setCandStatusArr(
        Array.from(
          new Set(mod_data.flatMap((cand) => cand.candStatus).filter((status) => status !== '-'))
        )
      );
      setCandCityArr(
        Array.from(
          new Set(mod_data.flatMap((cand) => cand.city).filter((status) => status !== '-'))
        )
      );
      setCandPrimrSklArr(
        Array.from(
          new Set(
            mod_data
              .flatMap((cand) => (cand.priSkl && cand.priSkl !== '-' ? cand.priSkl.split(',') : []))
              .map((skl) => skl.trim())
              .filter((skl) => skl !== '')
          )
        )
      );
      setCandNmArr(Array.from(new Set(mod_data.flatMap((cnd) => cnd.fname))));
      setCandEmailArr(Array.from(new Set(mod_data.flatMap((cnd) => cnd.email))));
      setCandPositionName(Array.from(new Set(mod_data.flatMap((cnd) => cnd.positionName))));
    } catch (error) {
      console.error('Error fetching candidate data:', error);
    } finally {
      setIsLoading(false); // Ensure loading stops regardless of success or failure
    }
  };

  useEffect(() => {
    getArrData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [subscrSiteId, candidateListRefresh]);

  useEffect(() => { }, [tableData]);

  const dataFiltered = applyFilter({
    inputData: tableData,
    comparator: getComparator(table.order, table.orderBy),
    filters,
  });

  const handleFormStatusChange = (status) => {
    setFilters((prevFilters) => {
      const updatedFormStatus = prevFilters.formStatus.includes(status)
        ? prevFilters.formStatus.filter((s) => s !== status) // Remove if already selected
        : [...prevFilters.formStatus, status]; // Add new selection

      return {
        ...prevFilters,
        formStatus: updatedFormStatus,
      };
    });
  };

  const dataInPage = dataFiltered.slice(
    table.page * table.rowsPerPage,
    table.page * table.rowsPerPage + table.rowsPerPage
  );

  const canReset = !isEqual(defaultFilters, filters);

  const notFound =
    (!dataFiltered.length && canReset && !isLoading) || (!dataFiltered.length && !isLoading);

  const handleFilters = useCallback(
    (name, value) => {
      const url = new URL(window.location);
      if (name === 'city' && (!value || value.length === 0)) {
        url.searchParams.delete('city');
        url.searchParams.delete('filterType');
        url.searchParams.delete('filterValue');
        window.history.replaceState({}, '', url.toString());
      }
      const newFilters = {
        ...filters,
        [name]: value,
      };
      setFiltersState(newFilters);
      dispatch(setPreservedFilters({ path: location.pathname, filters: newFilters }));
    },
    [dispatch, location.pathname, filters]
  );

  const handleResetFilters = useCallback(() => {
    const url = new URL(window.location);
    url.searchParams.delete('city');
    url.searchParams.delete('filterType');
    url.searchParams.delete('filterValue');
    window.history.replaceState({}, '', url.toString());

    const defaultState = { ...defaultFilters };
    setFiltersState(defaultState);
    dispatch(setPreservedFilters({ path: location.pathname, filters: defaultState }));
  }, [dispatch, location.pathname]);

  const { onResetPage } = table; // Destructure first

  useEffect(() => {
    onResetPage(); // Call the function
  }, [filters, onResetPage]); // Only track function and filter changes

  const handleEditRow = useCallback(
    async (id) => {
      const encryptedId = await fetchEncryptedToken(id, token);
      if (!encryptedId) throw new Error('Failed to encrypt ID');

      router.push(paths.dashboard.talentAcquisition.master.EditCandidate(encryptedId));
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [router]
  );
  const handleViewRow = useCallback(
    async (id) => {
      const encryptedId = await fetchEncryptedToken(id, token);
      if (!encryptedId) throw new Error('Failed to encrypt ID');

      router.push(paths.dashboard.talentAcquisition.master.ViewCandidate(encryptedId));
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [router]
  );

  // const handleScheduleRow = useCallback(
  //   async (id) => {
  //     router.push(`${paths.dashboard.talentAcquisition.calendarform}?candidateId=${id}`);
  //   },
  //   // eslint-disable-next-line react-hooks/exhaustive-deps
  //   [router]
  // );

  const handleUnblockRow = useCallback(
    async (id) => {
      const encryptedId = await fetchEncryptedToken(id, token);
      if (!encryptedId) throw new Error('Failed to encrypt ID');

      axiosInstance
        .put(`/rest/candidate/unblockCand/${encryptedId}`, null, {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        })
        .then((res) => {
          getArrData();
          enqueueSnackbar('Candidate Unblocked Successfully!');
        })
        .catch((error) => {
          handleApiError(error);
        });
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [enqueueSnackbar, token]
  );

  const handleDeleteRow = useCallback(
    async (id) => {
      const encryptedId = await fetchEncryptedToken(id, token);
      if (!encryptedId) throw new Error('Failed to encrypt ID');

      const response = await axiosInstance.put(`/rest/candidate/deleteCand/${encryptedId}`, null, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      enqueueSnackbar('Candidate Deleted!');
      const deleteRow = tableData.filter((row) => row.id !== id);
      setTableData(deleteRow);
      table.onUpdatePageDeleteRow(dataInPage.length);
      getArrData();
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [enqueueSnackbar, dataInPage.length, table, tableData, token]
  );

  // Define the table headers for specific columns

  const customCandTableHeaders = {
    id: 'Candidate Id',
    name: 'Candidate Name',
    email: 'Email',
    contact: 'Contact Number',
    gender: 'Gender',
    currentLoc: 'Current Location',
    prefLoc: 'Preferred Location',
    uniqNumTyp: 'Unique Number Type',
    uniqNum: 'Unique Number',
    linkedinUrl: 'LinkedIn Profile',
    readyToRelocate: 'Ready to Relocate',
    highestQual: 'Highest Qualification',
    priSkl: 'Primary Skills',
    secSkl: 'Secondary Skills',
    experience: 'Experience in Years',
    relExpYr: 'Relevant Experience',
    curComp: 'Current Employer',
    curJob: 'Current Job Title',
    noticePrd: 'Notice Period',
    curSal: 'Current Salary',
    expSal: 'Expected Salary',
    isCurEmp: 'Is Current Employee',
    resign: 'Has Resigned',
    resignDt: 'Resigned Date',
    canJoinBy: 'Can Join By',
    srcOfHire: 'Source Of Hire',
    srcOfHireNm: 'Source Of Hire Name',
    positionName: 'Approved For',
  };

  const customCandEduTableHeaders = {
    id: 'Candidate Id',
    name: 'Candidate Name',
    university: 'Institute/University',
    qualTyp: 'Qualification Type',
    qual: 'Qualification',
    stream: 'Stream',
    qualPercent: 'Percentage',
    cgpa: 'CGPA',
    isPursuing: 'Is Pursuing',
    frmDt: 'From Date',
    toDt: 'To Date',
  };
  const customCandCertTableHeaders = {
    id: 'Candidate Id',
    name: 'Candidate Name',
    certNm: 'Certification Name',
    institute: 'Institute',
    frmDt: 'From Date',
    toDt: 'To Date',
  };
  const customCandExpTableHeaders = {
    id: 'Candidate Id',
    name: 'Candidate Name',
    compNm: 'Company Name',
    desgn: 'Designation',
    ctc: 'CTC',
    compCnrty: 'Company Country',
    curWrking: 'Currently Working',
    frmDt: 'From Date',
    toDt: 'To Date',
  };
  const customCandRefTableHeaders = {
    id: 'Candidate Id',
    name: 'Candidate Name',
    refNm: 'Reference Name',
    orgNm: 'Organization Name',
    refDesgn: 'Reference Designation',
    refEmail: 'Reference Email',
    refContact: 'Reference Contact',
  };

  // Define the function to generate the Excel file
  async function generateExcelFile(data) {
    const workbook = new ExcelJS.Workbook();
    const worksheet = workbook.addWorksheet('Candidate');
    const worksheet2 = workbook.addWorksheet('Candidate Education');
    const worksheet3 = workbook.addWorksheet('Candidate Certification');
    const worksheet4 = workbook.addWorksheet('Candidate Experience');
    const worksheet5 = workbook.addWorksheet('Candidate Reference');
    // Set up the header row
    const headerRow = worksheet.addRow(Object.values(customCandTableHeaders));
    headerRow.font = { bold: true };
    headerRow.alignment = { horizontal: 'center' };
    headerRow.eachCell((cell) => {
      if (cell.value) {
        cell.fill = {
          type: 'pattern',
          pattern: 'solid',
          fgColor: { argb: 'FFCCE5FF' },
        };
      }
    });
    const headerRow2 = worksheet2.addRow(Object.values(customCandEduTableHeaders));
    headerRow2.font = { bold: true };
    headerRow2.alignment = { horizontal: 'center' };
    headerRow2.eachCell((cell) => {
      if (cell.value) {
        cell.fill = {
          type: 'pattern',
          pattern: 'solid',
          fgColor: { argb: 'FFCCE5FF' },
        };
      }
    });
    const headerRow3 = worksheet3.addRow(Object.values(customCandCertTableHeaders));
    headerRow3.font = { bold: true };
    headerRow3.alignment = { horizontal: 'center' };
    headerRow3.eachCell((cell) => {
      if (cell.value) {
        cell.fill = {
          type: 'pattern',
          pattern: 'solid',
          fgColor: { argb: 'FFCCE5FF' },
        };
      }
    });
    const headerRow4 = worksheet4.addRow(Object.values(customCandExpTableHeaders));
    headerRow4.font = { bold: true };
    headerRow4.alignment = { horizontal: 'center' };
    headerRow4.eachCell((cell) => {
      if (cell.value) {
        cell.fill = {
          type: 'pattern',
          pattern: 'solid',
          fgColor: { argb: 'FFCCE5FF' },
        };
      }
    });
    const headerRow5 = worksheet5.addRow(Object.values(customCandRefTableHeaders));
    headerRow5.font = { bold: true };
    headerRow5.alignment = { horizontal: 'center' };
    headerRow5.eachCell((cell) => {
      if (cell.value) {
        cell.fill = {
          type: 'pattern',
          pattern: 'solid',
          fgColor: { argb: 'FFCCE5FF' },
        };
      }
    });
    // Add data rows
    data.forEach((row) => {
      const dataRow = worksheet.addRow([]);

      // Use the customTableHeaders object to map column names to header labels
      dataRow.getCell(1).value = row.id || '-';
      dataRow.getCell(2).value = `${row.fname}` || '-';
      dataRow.getCell(3).value = row.email || '-';
      dataRow.getCell(4).value = `(${row.mobCode}) ${row.mob}` || '-';
      dataRow.getCell(5).value = row.gender || '-';
      dataRow.getCell(6).value = row.city || '-';
      dataRow.getCell(7).value = Array.isArray(row.prefLoc)
        ? row.prefLoc.join(', ')
        : row.prefLoc || '-';
      dataRow.getCell(8).value = row.uniqNumTyp || '-';
      dataRow.getCell(9).value = decryptData(row.uniqNum) || '-';
      dataRow.getCell(10).value = decryptData(row.linkedinUrl) || '-';
      dataRow.getCell(11).value = row.readyToRelocate || 'FALSE';
      dataRow.getCell(12).value = row.highestQual || '-';
      dataRow.getCell(13).value = row.priSkl || '-';
      dataRow.getCell(14).value = row.secSkl || '-';
      dataRow.getCell(15).value = row.experience || '-';
      dataRow.getCell(16).value = row.relExpYr || '-';
      dataRow.getCell(17).value = row.curComp || '-';
      dataRow.getCell(18).value = row.curJob || '-';
      dataRow.getCell(19).value = row.noticePrd || '-';
      dataRow.getCell(20).value = `${row.curSalCurncy}${row.curSal}` || '-';
      dataRow.getCell(21).value = `${row.expSalCurncy}${row.expSal}` || '-';
      dataRow.getCell(22).value = row.isCurEmp || 'FALSE';
      dataRow.getCell(23).value = row.resign || '-';
      dataRow.getCell(24).value = row.resignDt ? formatDtTm(row.resignDt) : '-';
      dataRow.getCell(25).value = row.canJoinBy ? formatDtTm(row.canJoinBy) : '-';
      dataRow.getCell(26).value = row.srcOfHire || '-';
      dataRow.getCell(27).value = row.srcOfHireNm || '-';
      dataRow.getCell(28).value = row.positionName || '-';

      if (row.eduDetails && row.eduDetails.length > 0) {
        row.eduDetails.forEach((r) => {
          const dataRow2 = worksheet2.addRow([]);
          dataRow2.getCell(1).value = row.id || '-';
          dataRow2.getCell(2).value = `${row.fname}` || '-';
          dataRow2.getCell(3).value = r?.university || '-';
          dataRow2.getCell(4).value = r?.qualificationTypeId || '-';
          dataRow2.getCell(5).value = r?.qualification || '-';
          dataRow2.getCell(6).value = r?.stream || '-';
          dataRow2.getCell(7).value = r.candidateQualPercentage || '-';
          dataRow2.getCell(8).value = r.candidateQualCgpa || '-';
          // dataRow2.getCell(9).value = r.currentlyPursuing || 'FALSE';
          dataRow2.getCell(9).value = r.currentlyPursuing ? 'Yes' : 'No';
          dataRow2.getCell(10).value = r.candidateQualFromDate
            ? formatDtTm(r.candidateQualFromDate)
            : '-';
          dataRow2.getCell(11).value = r.candidateQualToDate
            ? formatDtTm(r.candidateQualToDate)
            : '-';
        });
      } else {
        const dataRow2 = worksheet2.addRow([]);
        dataRow2.getCell(1).value = row.id || '-';
        dataRow2.getCell(2).value = `${row.fname}` || '-';
        dataRow2.getCell(3).value = '-';
        dataRow2.getCell(4).value = '-';
        dataRow2.getCell(5).value = '-';
        dataRow2.getCell(6).value = '-';
        dataRow2.getCell(7).value = '-';
        dataRow2.getCell(8).value = '-';
        dataRow2.getCell(9).value = '-';
        dataRow2.getCell(10).value = '-';
        dataRow2.getCell(11).value = '-';
      }
      if (row.certDetails && row.certDetails.length > 0) {
        row.certDetails.forEach((r) => {
          const dataRow3 = worksheet3.addRow([]);
          dataRow3.getCell(1).value = row.id || '-';
          dataRow3.getCell(2).value = `${row.fname}` || '-';
          dataRow3.getCell(3).value = r.certificationName || '-';
          dataRow3.getCell(4).value = r.instituteName || '-';
          dataRow3.getCell(5).value = r.durationFrom ? formatDtTm(r.durationFrom) : '-';
          dataRow3.getCell(6).value = r.durationTo ? formatDtTm(r.durationTo) : '-';
        });
      } else {
        const dataRow3 = worksheet3.addRow([]);
        dataRow3.getCell(1).value = row.id || '-';
        dataRow3.getCell(2).value = `${row.fname}` || '-';
        dataRow3.getCell(3).value = '-';
        dataRow3.getCell(4).value = '-';
        dataRow3.getCell(5).value = '-';
        dataRow3.getCell(6).value = '-';
      }
      if (row.expDetails && row.expDetails.length > 0) {
        row.expDetails.forEach((r) => {
          const dataRow4 = worksheet4.addRow([]);
          dataRow4.getCell(1).value = row.id || '-';
          dataRow4.getCell(2).value = `${row.fname}` || '-';
          dataRow4.getCell(3).value = decryptData(r.candidateExperienceCompanyName) || '-';
          dataRow4.getCell(4).value = r?.candidateDesignation.masterName || '-';
          // dataRow4.getCell(5).value = r.candidateCtc || '-';
          dataRow4.getCell(5).value = `${r?.candidateCtcCurrency?.currencySymbol || ''} ${decryptData(r?.candidateCtc) || ''
            }`.trim();
          dataRow4.getCell(5).value = `${r?.candidateCtcCurrency?.currencySymbol || ''} ${decryptData(r?.candidateCtc) || ''
            }`.trim();
          dataRow4.getCell(6).value = r?.candidateExperienceCountryid?.countryName || '-';
          dataRow4.getCell(7).value = r.candidateCurrentlyWorking ? 'Yes' : 'No' || '-';
          dataRow4.getCell(8).value = r.candidateExperienceFromDate
            ? formatDtTm(r.candidateExperienceFromDate)
            : '-';
          dataRow4.getCell(9).value = r.candidateExperienceToDate
            ? formatDtTm(r.candidateExperienceToDate)
            : '-';
        });
      } else {
        const dataRow4 = worksheet4.addRow([]);
        dataRow4.getCell(1).value = row.id || '-';
        dataRow4.getCell(2).value = `${row.fname}` || '-';
        dataRow4.getCell(3).value = '-';
        dataRow4.getCell(4).value = '-';
        dataRow4.getCell(5).value = '-';
        dataRow4.getCell(6).value = '-';
        dataRow4.getCell(7).value = '-';
        dataRow4.getCell(8).value = '-';
        dataRow4.getCell(9).value = '-';
      }
      if (row.refDetails && row.refDetails.length > 0) {
        row.refDetails.forEach((r) => {
          const dataRow5 = worksheet5.addRow([]);
          dataRow5.getCell(1).value = row.id || '-';
          dataRow5.getCell(2).value = `${row.fname}` || '-';
          dataRow5.getCell(3).value = decryptData(r.candidateReferenceName) || '-';
          dataRow5.getCell(4).value = decryptData(r.candidateReferenceOrganizationName) || '-';
          dataRow5.getCell(5).value = r?.candidateReferenceDesignationId || '-';
          dataRow5.getCell(6).value = decryptData(r.candidateReferenceEmail) || '-';
          dataRow5.getCell(7).value = decryptData(r.candidateReferenceContactNumber) || '-';
        });
      } else {
        const dataRow5 = worksheet5.addRow([]);
        dataRow5.getCell(1).value = row.id || '-';
        dataRow5.getCell(2).value = `${row.fname}` || '-';
        dataRow5.getCell(3).value = '-';
        dataRow5.getCell(4).value = '-';
        dataRow5.getCell(5).value = '-';
        dataRow5.getCell(6).value = '-';
        dataRow5.getCell(7).value = '-';
      }
      const alignmentOptions = {
        horizontal: 'left',
        vertical: 'center',
        wrapText: true,
      };
      dataRow.eachCell((cell, colNumber) => {
        cell.alignment = alignmentOptions;
      });
    });

    // Set up the workbook properties
    // worksheet.properties.defaultRowHeight = 18;
    worksheet.properties.defaultColWidth = 25;
    worksheet2.properties.defaultColWidth = 25;
    worksheet3.properties.defaultColWidth = 25;
    worksheet4.properties.defaultColWidth = 25;
    worksheet5.properties.defaultColWidth = 25;
    // Save the workbook as a blob
    const buffer = await workbook.xlsx.writeBuffer();
    const blob = new Blob([buffer], {
      type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    });
    // Save the blob as a file
    saveAs(blob, 'Candidate Master.xlsx');
  }

  const renderFilters = (
    <Stack
      spacing={3}
      justifyContent="space-between"
      alignItems={{ xs: 'flex-end', sm: 'center' }}
      direction={{ xs: 'column', sm: 'row' }}
    >
      <Stack direction="row" spacing={1} flexShrink={0}>
        <CandListFilter
          open={openFilters.value}
          onOpen={openFilters.onTrue}
          onClose={openFilters.onFalse}
          filters={filters || defaultFilters}
          onFilters={handleFilters}
          canReset={canReset}
          onResetFilters={handleResetFilters}
          genderOpt={genderArr.sort()}
          srcHireOpt={srcHireArr.sort()}
          candStatusOpt={candStatusArr.sort()}
          candCityOpt={candCityArr.sort()}
          candSklOpt={candPrimrSklArr.sort()}
          candNmOpt={candNmArr.sort()}
          candEmailOpt={candEmailArr.sort()}
          candPostionName={candPostionName.sort()}
          statusOptions={['Active', 'Inactive']}
          qualOpt={qualArr.sort()}
          streamOpt={streamArr.sort()}
          experienceOpt={experienceArr.sort()}
          onFormStatusChange={handleFormStatusChange}
        />
      </Stack>
    </Stack>
  );

  const renderResults = (
    <CandListFilterResult
      filters={filters}
      onResetFilters={handleResetFilters}
      canReset={canReset}
      onFilters={handleFilters}
      results={dataFiltered.length}
    />
  );

  return (
    <>
      <Container maxWidth={settings.themeStretch ? false : 'lg'}>
        <BreadCrumbs
          heading="Candidate Pool"
          action={
            fullAccess ? (
              <Box
                onClickCapture={(e) => {
                  if (isLimitReached) {
                    e.preventDefault();
                    e.stopPropagation();
                    dispatch(
                      openUpgradeDialog({
                        requiredPlan,
                        featureLabel: FEATURE_LABELS[FeatureCode.MAX_CANDIDATE_POOL],
                        featureType,
                        limitValue: maxCandidatePool,
                      })
                    );
                  }
                }}
              >
                <Button
                  component={isLimitReached ? 'button' : RouterLink}
                  href={isLimitReached ? undefined : paths.dashboard.talentAcquisition.master.NewCandidate}
                  color="secondary"
                  variant="soft"
                  startIcon={<Iconify icon="mingcute:add-line" />}
                >
                  New Candidate
                  {isLimitReached && (
                    <PlanBadge plan={requiredPlan} isLocked sx={{ ml: 1 }} />
                  )}
                </Button>
              </Box>
            ) : null
          }
        />

        {/* --- UPDATED LAYOUT STARTS HERE --- */}

        <Stack
          direction={{ xs: 'column', md: 'row' }}
          alignItems="flex-start"
          justifyContent="space-between"
          spacing={2}
          sx={{ mb: "12px" }}
        >
          {/* Left Side: Filter Results */}
          <Box sx={{ flexGrow: 1, minWidth: 0 }}>{canReset && renderResults}</Box>

          {/* Right Side: Action Buttons */}
          <Stack
            direction="row"
            alignItems="center"
            justifyContent="flex-end"
            spacing={1.5}
            flexShrink={0}
            sx={{
              pt: { md: 0.5 },
              width: { xs: '100%', md: 'auto' }, // Ensures flex-end pushes items to the right on mobile
              overflowX: 'auto', // Prevents layout breaking on extremely small screens
            }}
          >
            <Button
              component={RouterLink}
              href={paths.dashboard.talentAcquisition.master.ListOfUnderReviewedCand}
              color="info"
              variant="soft"
              startIcon={<Iconify icon="carbon:view" />}
              sx={{
                px: 2.5,
                fontWeight: 600,
                whiteSpace: 'nowrap', // Prevents "Under Review" and "List" from stacking
                minWidth: 'max-content', // Ensures the button maintains its shape
              }}
            >
              Under Review List
            </Button>

            {fullAccess && (
              <Box
                onClickCapture={(e) => {
                  if (isLimitReached) {
                    e.preventDefault();
                    e.stopPropagation();
                    dispatch(
                      openUpgradeDialog({
                        requiredPlan,
                        featureLabel: FEATURE_LABELS[FeatureCode.MAX_CANDIDATE_POOL],
                        featureType,
                        limitValue: maxCandidatePool,
                      })
                    );
                  }
                }}
              >
                <Tooltip title="Coming Soon" placement="top" arrow>
                  <span style={{ display: 'inline-flex' }}>
                    <IconButton
                      disabled
                      sx={{
                        cursor: 'not-allowed',
                      }}
                    >
                      <Iconify
                        icon="bi:database-fill-up"
                        width={22}
                        height={22}
                        style={{ color: '#9e9e9e' }}
                      />
                    </IconButton>
                  </span>
                </Tooltip>
              </Box>
            )}

            <Tooltip title="Download Excel" placement="top" arrow>
              <IconButton
                onClick={() => generateExcelFile(dataFiltered)}
                sx={{
                  '&:hover': { bgcolor: 'action.selected' },
                }}
              >
                <Iconify icon="vscode-icons:file-type-excel" width={24} height={24} />
              </IconButton>
            </Tooltip>

            {renderFilters}
          </Stack>
        </Stack>

        {/* --- UPDATED LAYOUT ENDS HERE --- */}
        <Card>
          {/* <CandTableToolbar filters={filters} onFilters={handleFilters} /> */}
          <TableContainer sx={{ position: 'relative', overflow: 'unset' }}>
            <Scrollbar>
              <Table size={table.dense ? 'small' : 'medium'} sx={{ minWidth: 960 }}>
                <TableHeadCustom
                  order={table.order}
                  orderBy={table.orderBy}
                  headLabel={TABLE_HEAD}
                  rowCount={tableData.length}
                  onSort={table.onSort}
                />
                <TableBody>
                  {isLoading
                    ? [...Array(table.rowsPerPage)].map((i, index) => (
                      <TableSkeleton
                        key={index}
                        sx={{ height: denseHeight }}
                        columns={TABLE_HEAD}
                      />
                    ))
                    : dataFiltered
                      .slice(
                        table.page * table.rowsPerPage,
                        table.page * table.rowsPerPage + table.rowsPerPage
                      )
                      .map((row) => (
                        <CandTableRow
                          key={row.id}
                          row={row}
                          onDeleteRow={() => handleDeleteRow(row.id)}
                          onEditRow={() => handleEditRow(row.id)}
                          onViewRow={() => handleViewRow(row.id)}
                          // onScheduleRow={() => handleScheduleRow(row.id)}
                          onUnblockRow={() => handleUnblockRow(row.id)}
                          fullAccess={fullAccess}
                          getArrData={() => getArrData()}
                        />
                      ))}
                  {/* 1. Only show the illustration if there is no data */}
                  {!isLoading && !isFirstLoad && dataFiltered.length === 0 && (
                    <TableNoData notFound={notFound} />
                  )}

                  {/* 2. ONLY render the empty filler rows if there is actual data present */}
                  {!isLoading && dataFiltered.length > 0 && (
                    <TableEmptyRows
                      height={45}
                      emptyRows={Math.max(0, table.rowsPerPage - dataInPage.length)}
                    />
                  )}

                  <TableNoData notFound={notFound} />
                </TableBody>
              </Table>
            </Scrollbar>
          </TableContainer>
          <TablePaginationCustom
            count={dataFiltered.length}
            page={table.page}
            rowsPerPage={table.rowsPerPage}
            onPageChange={table.onChangePage}
            onRowsPerPageChange={table.onChangeRowsPerPage}
            //
            dense={table.dense}
            onChangeDense={table.onChangeDense}
          />
        </Card>
      </Container>
      <ImportExcelDialog
        open={uploadCsv.value}
        onClose={uploadCsv.onFalse}
        screen="Candidate"
        candidateListRefresh={candidateListRefresh}
        setCandidateListRefresh={setCandidateListRefresh}
      />
    </>
  );
}
CandidateListView.propTypes = {
  fullAccess: PropTypes.bool,
};

function applyFilter({ inputData, comparator, filters }) {
  const {
    fname,
    email,
    gender,
    sourceOfHire,
    candStatus,
    city,
    primarySkill,
    Status,
    qualificationNames,
    stream,
    experience,
    formStatus = [],
  } = filters;
  const stabilizedThis = inputData.map((el, index) => [el, index]);
  stabilizedThis.sort((a, b) => {
    const order = comparator(a[0], b[0]);
    if (order !== 0) return order;
    return a[1] - b[1];
  });

  const today = new Date();

  inputData = stabilizedThis.map((el) => el[0]);
  inputData = inputData.filter((row) => {
    const isNameMatch = fname.length === 0 || fname.some((item) => row.fname === item);
    const isEmailMatch = email.length === 0 || email.some((item) => row.email === item);
    const isGenderMatch = gender.length === 0 || gender.some((item) => row.gender === item);
    const isQualificationMatch =
      qualificationNames.length === 0 || qualificationNames.includes(row.qualification);
    const isStreamMatch = stream.length === 0 || stream.includes(row.stream);
    const isSourceMatch =
      sourceOfHire.length === 0 || sourceOfHire.some((item) => row.srcOfHire === item);
    const isStatusMatch =
      candStatus.length === 0 || candStatus.some((item) => row.candStatus.includes(item));
    const isCityMatch =
      city.length === 0 ||
      city.some((item) => row.city && row.city.trim().toLowerCase() === String(item).trim().toLowerCase());
    // const isPositionMatch = positionArr.length === 0 || positionArr.some((item) => row.jd.includes(item));

    const isPrimarySkillMatch =
      primarySkill.length === 0 ||
      primarySkill.some((skill) => {
        if (!row.priSkl || !skill) return false; // guard against undefined
        const skillName =
          typeof skill === 'string' ? skill : skill.skillId || skill.skillName || '';
        if (!skillName) return false;
        return row.priSkl
          .split(',')
          .some((rowSkill) => rowSkill.trim().toLowerCase() === skillName.trim().toLowerCase());
      });

    // const isExperienceMatch = experience.length === 0 || experience.some((item) => row.experience === item);
    const isExperienceMatch =
      (!filters.experience.from || row.experience >= Number(filters.experience.from)) &&
      (!filters.experience.to || row.experience <= Number(filters.experience.to));

    // Handle Final (Complete/Incomplete) Status
    let formStatusMatch = true;
    if (filters.formStatus.length > 0) {
      formStatusMatch = filters.formStatus.includes(row.isFinal);
    }

    let isEndDateMatch = true;

    if (Status) {
      switch (true) {
        case Status.includes('Active') && Status.includes('Inactive'):
          // If both "Active" and "Inactive" are selected, show all rows without filtering by status
          isEndDateMatch = true;
          break;

        case Status.includes('Active'):
          // Active: endDate >= today or null
          isEndDateMatch =
            isEndDateMatch &&
            (row.endDate === '-' || new Date(row.endDate).getTime() >= today.getTime());
          break;

        case Status.includes('Inactive'):
          // Inactive: endDate < today
          isEndDateMatch = isEndDateMatch && new Date(row.endDate).getTime() <= today.getTime();
          break;

        default:
          // No specific status filter applied
          isEndDateMatch = true;
          break;
      }
    }

    return (
      isGenderMatch &&
      isSourceMatch &&
      isStatusMatch &&
      isCityMatch &&
      isPrimarySkillMatch &&
      isQualificationMatch &&
      isStreamMatch &&
      isEmailMatch &&
      isNameMatch &&
      isEndDateMatch &&
      isExperienceMatch &&
      formStatusMatch
    );
  });
  return inputData;
}
